import { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import Modal from './Modal';
import StockChangeModal from './StockChangeModal';
import { SkeletonTable } from './Skeleton';
import { formatDate as formatDateUtil, toDateInputValue } from '../utils/dateFormatter';
import { Stone } from '../types';
import { groupStonesByBaseName, filterStoneGroups, getSizeLabel } from '../utils/stoneMatrixHelper';

interface StoneConsumption {
  id: number;
  week_start_date: string;
  stone_id: number;
  qty_ristras: number;
  created_at: string;
  updated_at: string;
  stone: {
    id: number;
    cod_piedra: string;
    nombre_piedra: string;
    type?: string;
    size_mm?: number;
  };
}

// Función helper para obtener el viernes de una semana
const getFridayOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  // Viernes es el día 5 (0=domingo, 1=lunes, ..., 5=viernes)
  // Calcular la diferencia para llegar al viernes de la semana actual
  const diff = d.getDate() - day + 5;
  return new Date(d.setDate(diff));
};

// Función helper para formatear fecha como YYYY-MM-DD (en zona horaria de Madrid)
const formatDate = (date: Date): string => {
  return toDateInputValue(date);
};

export default function StoneConsumption() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const [stones, setStones] = useState<Stone[]>([]);
  const [consumptions, setConsumptions] = useState<StoneConsumption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<string>('');
  const [consumptionInputs, setConsumptionInputs] = useState<Map<string, string>>(new Map()); // Map<"stoneId-size", value>
  const [editingConsumption, setEditingConsumption] = useState<StoneConsumption | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isStockChangeModalOpen, setIsStockChangeModalOpen] = useState(false);
  const [stockChanges, setStockChanges] = useState<Array<{ item_code: string; item_name: string; location_name: string; stock_before: number; stock_after: number; quantity_changed: number }>>([]);

  useEffect(() => {
    loadStones();
    loadConsumptions();
    // Pre-llenar con la semana actual (viernes)
    const friday = getFridayOfWeek(new Date());
    setSelectedWeek(formatDate(friday));
  }, []);

  const loadStones = async () => {
    try {
      const response = await api.get('/stones');
      setStones(response.data.filter((stone: Stone) => stone.is_active));
    } catch (err: any) {
      console.error('Error al cargar piedras:', err);
    }
  };

  const loadConsumptions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/stone-consumptions');
      setConsumptions(response.data);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar consumos');
    } finally {
      setLoading(false);
    }
  };

  const loadConsumptionsByWeek = async (weekStartDate: string) => {
    try {
      const response = await api.get(`/stone-consumptions/week/${weekStartDate}`);
      // Convertir a mapa para fácil acceso: clave = "stoneId-size"
      const newMap = new Map<string, string>();
      response.data.forEach((cons: StoneConsumption) => {
        // Necesitamos obtener el tamaño de la piedra
        const stone = stones.find(s => s.id === cons.stone_id);
        if (stone) {
          let size = stone.size_mm ? Math.round(parseFloat(stone.size_mm.toString())) : 0;
          // Intentar extraer del código o nombre si no está en size_mm
          if (!size || size === 0) {
            const codMatch = stone.cod_piedra.match(/-(\d+)mm$/i);
            if (codMatch) {
              size = parseInt(codMatch[1], 10);
            } else {
              const nombreMatch = stone.nombre_piedra.match(/\s+(\d+)mm$/i);
              if (nombreMatch) {
                size = parseInt(nombreMatch[1], 10);
              }
            }
          }
          const key = `${cons.stone_id}-${size}`;
          newMap.set(key, cons.qty_ristras.toString());
        }
      });
      setConsumptionInputs(newMap);
    } catch (err: any) {
      // Si no hay consumos para esa semana, simplemente limpiar los inputs
      setConsumptionInputs(new Map());
    }
  };

  const handleOpenCreateModal = () => {
    const friday = getFridayOfWeek(new Date());
    const weekDate = formatDate(friday);
    setSelectedWeek(weekDate);
    setConsumptionInputs(new Map());
    loadConsumptionsByWeek(weekDate);
    setIsCreateModalOpen(true);
  };

  const handleDownloadTemplatePDF = () => {
    // Obtener las piedras agrupadas igual que en el modal
    const { groups, allSizes } = groupStonesByBaseName(stones.filter(s => s.is_active));
    
    // Dividir los grupos en dos partes aproximadamente iguales
    const midPoint = Math.ceil(groups.length / 2);
    const firstPageGroups = groups.slice(0, midPoint);
    const secondPageGroups = groups.slice(midPoint);
    
    // Crear un HTML oculto con la plantilla
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showError('No se pudo abrir la ventana de impresión. Verifica que los pop-ups estén permitidos.');
      return;
    }

    const friday = getFridayOfWeek(new Date());
    const weekDate = formatDateUtil(friday);
    // weekDate ya está en formato dd/mm/yyyy, solo necesitamos mostrarlo
    const weekDateFormatted = weekDate;

    const renderTable = (pageGroups: typeof groups, pageNumber: number) => {
      return `
        <div class="page">
          <h1>PLANTILLA DE CONSUMO DE PIEDRAS (RISTRAS) - PÁGINA ${pageNumber}</h1>
          <div class="week-info">
            <strong>Semana (Viernes):</strong> ${weekDateFormatted}
          </div>
          <table>
            <thead>
              <tr>
                <th style="max-width: 120px; width: 120px;">NOMBRE</th>
                ${allSizes.map((size) => `<th style="width: 35px;">${getSizeLabel(size)}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${pageGroups.map((group) => {
                return `
                  <tr>
                    <td class="name-cell">${group.nombre_piedra}</td>
                    ${allSizes.map((size) => {
                      const stonesForSize = group.sizes.get(size) || [];
                      if (stonesForSize.length === 0) {
                        return '<td class="empty-cell">-</td>';
                      }
                      return '<td class="input-cell"></td>';
                    }).join('')}
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          ${pageNumber === 2 ? `
            <div class="instructions">
              <p style="margin: 0;"><strong>Instrucciones:</strong> Anota la cantidad de ristras consumidas de cada piedra en la casilla correspondiente.</p>
            </div>
          ` : ''}
        </div>
      `;
    };

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Plantilla de Consumo de Piedras</title>
        <style>
          @media print {
            @page {
              size: A4 portrait;
              margin: 0.5cm;
            }
            .page {
              page-break-after: always;
            }
            .page:last-child {
              page-break-after: auto;
            }
            body {
              margin: 0;
              padding: 0;
            }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            padding: 10px;
            margin: 0;
            font-size: 13px;
          }
          .page {
            min-height: calc(100vh - 1cm);
            display: flex;
            flex-direction: column;
          }
          h1 {
            text-align: center;
            margin: 0 0 10px 0;
            font-size: 22px;
            font-weight: bold;
          }
          .week-info {
            text-align: center;
            margin-bottom: 12px;
            font-size: 13px;
            color: #666;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 5px;
            font-size: 12px;
            flex: 1;
          }
          th {
            background-color: #f5f5f5;
            border: 1px solid #000;
            padding: 6px 4px;
            text-align: center;
            font-weight: bold;
            font-size: 11px;
          }
          td {
            border: 1px solid #000;
            padding: 5px;
            text-align: center;
            font-size: 12px;
          }
          td.name-cell {
            text-align: left;
            font-weight: bold;
            font-size: 11px;
            padding: 5px 8px;
            max-width: 160px;
            word-wrap: break-word;
          }
          td.input-cell {
            padding: 5px;
            width: 45px;
            height: 26px;
            min-width: 45px;
          }
          .empty-cell {
            border: 1px solid #ccc;
            background-color: #fafafa;
            padding: 5px;
            width: 45px;
          }
          .instructions {
            margin-top: 12px;
            font-size: 11px;
            color: #666;
            padding: 0;
          }
        </style>
      </head>
      <body>
        ${renderTable(firstPageGroups, 1)}
        ${renderTable(secondPageGroups, 2)}
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    
    // Esperar a que se cargue el contenido y luego imprimir
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  const handleWeekChange = (weekDate: string) => {
    setSelectedWeek(weekDate);
    loadConsumptionsByWeek(weekDate);
  };

  const handleConsumptionInputChange = (stoneId: number, size: number, value: string) => {
    const newMap = new Map(consumptionInputs);
    const key = `${stoneId}-${size}`;
    if (value === '' || value === '0') {
      newMap.delete(key);
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 0) {
        newMap.set(key, value);
      }
    }
    setConsumptionInputs(newMap);
  };

  const handleCreateWeeklyConsumptions = async () => {
    if (!selectedWeek) {
      showError('Debes seleccionar una semana');
      return;
    }

    // Validar que la fecha sea un viernes
    const date = new Date(selectedWeek);
    const dayOfWeek = date.getDay();
    if (dayOfWeek !== 5) {
      showError('La fecha debe ser un viernes');
      return;
    }

    if (consumptionInputs.size === 0) {
      showError('Debes registrar al menos un consumo');
      return;
    }

    try {

      const consumptions = Array.from(consumptionInputs.entries()).map(([key, qty]) => {
        const [stone_id] = key.split('-').map(Number);
        return {
          stone_id,
          qty_ristras: parseInt(qty, 10),
        };
      });

      const response = await api.post('/stone-consumptions/weekly', {
        week_start_date: selectedWeek,
        consumptions,
      });

      showSuccess('Consumos registrados correctamente');
      
      if (response.data.stockChanges && response.data.stockChanges.length > 0) {
        setStockChanges(response.data.stockChanges);
        setIsStockChangeModalOpen(true);
      }
      
      setIsCreateModalOpen(false);
      setConsumptionInputs(new Map());
      loadConsumptions();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al registrar consumos');
    }
  };

  const handleOpenEditModal = (consumption: StoneConsumption) => {
    setEditingConsumption(consumption);
    setIsEditModalOpen(true);
  };

  const handleUpdateConsumption = async (qty: number) => {
    if (!editingConsumption) return;

    if (qty < 0) {
      showError('La cantidad no puede ser negativa');
      return;
    }

    try {
      await api.put(`/stone-consumptions/${editingConsumption.id}`, {
        qty_ristras: qty,
      });
      showSuccess('Consumo actualizado correctamente');
      setIsEditModalOpen(false);
      setEditingConsumption(null);
      loadConsumptions();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al actualizar consumo');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este consumo?')) {
      return;
    }

    try {
      await api.delete(`/stone-consumptions/${id}`);
      showSuccess('Consumo eliminado correctamente');
      loadConsumptions();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar consumo');
    }
  };

  // Agrupar consumos por semana
  const consumptionsByWeek = new Map<string, StoneConsumption[]>();
  consumptions.forEach((cons) => {
    const week = cons.week_start_date;
    if (!consumptionsByWeek.has(week)) {
      consumptionsByWeek.set(week, []);
    }
    consumptionsByWeek.get(week)!.push(cons);
  });

  if (loading) {
    return (
      <div className="tab-content">
        <SkeletonTable rows={8} columns={5} />
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="flex-between mb-2">
        <h2>Consumo de Piedras</h2>
        {canEdit('stone-consumption') && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={handleDownloadTemplatePDF}>
              📄 Descargar Plantilla
            </button>
            <button className="btn btn-primary" onClick={handleOpenCreateModal}>
              + Registrar Consumo Semanal
            </button>
          </div>
        )}
      </div>


      <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          <strong>Instrucciones:</strong> Registra semanalmente la cantidad de ristras de cada piedra que se han consumido.
          El sistema calculará automáticamente los mínimos de stock basándose en el consumo histórico.
        </p>
      </div>

      <div className="table-container">
        {consumptionsByWeek.size === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
            No hay consumos registrados
          </div>
        ) : (
          Array.from(consumptionsByWeek.entries())
            .sort((a, b) => b[0].localeCompare(a[0])) // Ordenar por fecha descendente
            .map(([week, weekConsumptions]) => {
              // Convertir consumos a formato de piedras para agrupar
              const stonesFromConsumptions = weekConsumptions.map(cons => ({
                id: cons.stone.id,
                cod_piedra: cons.stone.cod_piedra,
                nombre_piedra: cons.stone.nombre_piedra,
                type: cons.stone.type,
                size_mm: cons.stone.size_mm,
                coste_piedra: undefined,
                proveedor: undefined,
                is_top_ventas: false,
                is_active: true,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              }));

              const { groups, allSizes } = groupStonesByBaseName(stonesFromConsumptions);
              const consumptionMap = new Map<number, StoneConsumption>();
              weekConsumptions.forEach(cons => {
                consumptionMap.set(cons.stone_id, cons);
              });

              return (
                <div key={week} style={{ marginBottom: '2rem' }}>
                  <h3 style={{ marginBottom: '1rem' }}>
                    Semana del {formatDateUtil(new Date(week))}
                  </h3>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        {allSizes.map((size) => (
                          <th key={size} style={{ textAlign: 'center' }}>
                            {getSizeLabel(size)}
                          </th>
                        ))}
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map((group) => {
                        // Encontrar el primer consumo para obtener el ID (todos los de un grupo comparten la misma semana)
                        let firstConsumption: StoneConsumption | null = null;
                        group.sizes.forEach((stonesForSize) => {
                          stonesForSize.forEach(stone => {
                            const cons = consumptionMap.get(stone.id);
                            if (cons && !firstConsumption) {
                              firstConsumption = cons;
                            }
                          });
                        });

                        return (
                          <tr key={`${group.cod_piedra}-${group.nombre_piedra}`}>
                            <td>
                              <strong>{group.nombre_piedra}</strong>
                            </td>
                            {allSizes.map((size) => {
                              const stonesForSize = group.sizes.get(size) || [];
                              if (stonesForSize.length === 0) {
                                return (
                                  <td key={size} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                                    -
                                  </td>
                                );
                              }
                              const stone = stonesForSize[0];
                              const cons = consumptionMap.get(stone.id);
                              return (
                                <td key={size} style={{ textAlign: 'center' }}>
                                  {cons ? (
                                    <strong>{cons.qty_ristras}</strong>
                                  ) : null}
                                </td>
                              );
                            })}
                            <td>
                              {firstConsumption && (
                                canEdit('stone-consumption') ? (
                                  <div className="flex gap-1">
                                    <button
                                      className="btn btn-secondary"
                                      onClick={() => handleOpenEditModal(firstConsumption!)}
                                      style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                                    >
                                      Editar
                                    </button>
                                    <button
                                      className="btn btn-danger"
                                      onClick={() => handleDelete(firstConsumption!.id)}
                                      style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                                    >
                                      Eliminar
                                    </button>
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                                    Sin permisos
                                  </span>
                                )
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })
        )}
      </div>

      {/* Modal Crear Consumo Semanal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Registrar Consumo Semanal"
        size="large"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setIsCreateModalOpen(false)}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCreateWeeklyConsumptions}
              data-modal-default="true"
            >
              Guardar Consumos
            </button>
          </>
        }
      >
        <div className="input-group">
          <label className="input-label">Semana (Viernes) *</label>
          <input
            type="date"
            className="input"
            value={selectedWeek}
            onChange={(e) => handleWeekChange(e.target.value)}
            required
          />
          <small style={{ color: 'var(--text-secondary)', marginTop: '0.25rem', display: 'block' }}>
            Selecciona el viernes de la semana a registrar
          </small>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            className="input"
            placeholder="Buscar por código o nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', maxWidth: '400px' }}
          />
        </div>

        <h3 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
          Consumos de Piedras (Ristras)
        </h3>
        <div className="table-container" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {(() => {
            const { groups, allSizes } = groupStonesByBaseName(stones.filter(s => s.is_active));
            const filteredGroups = filterStoneGroups(groups, searchTerm);

            return (
              <table className="table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    {allSizes.map((size) => (
                      <th key={size} style={{ textAlign: 'center' }}>
                        {getSizeLabel(size)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredGroups.length === 0 ? (
                    <tr>
                      <td colSpan={allSizes.length + 1} className="text-center">
                        {searchTerm ? 'No se encontraron piedras que coincidan con la búsqueda' : 'No hay piedras activas'}
                      </td>
                    </tr>
                  ) : (
                    filteredGroups.map((group) => (
                      <tr key={`${group.cod_piedra}-${group.nombre_piedra}`}>
                        <td>
                          <strong>{group.nombre_piedra}</strong>
                        </td>
                        {allSizes.map((size) => {
                          const stonesForSize = group.sizes.get(size) || [];
                          if (stonesForSize.length === 0) {
                            return (
                              <td key={size} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                                -
                              </td>
                            );
                          }
                          const stone = stonesForSize[0];
                          const key = `${stone.id}-${size}`;
                          const currentValue = consumptionInputs.get(key) || '';
                          return (
                            <td key={size} style={{ textAlign: 'center' }}>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                className="input"
                                value={currentValue}
                                onChange={(e) => handleConsumptionInputChange(stone.id, size, e.target.value)}
                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                placeholder="0"
                                style={{ width: '80px', textAlign: 'center' }}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            );
          })()}
        </div>

        {consumptionInputs.size > 0 && (
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
            <strong>
              Total: {consumptionInputs.size} piedra(s) con consumo registrado
            </strong>
            <br />
            <small style={{ color: 'var(--text-secondary)' }}>
              Total de ristras: {Array.from(consumptionInputs.values())
                .reduce((sum, qty) => sum + parseInt(qty, 10), 0)} ristras
            </small>
          </div>
        )}
      </Modal>

      {/* Modal Editar Consumo */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingConsumption(null);
        }}
        title="Editar Consumo"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsEditModalOpen(false);
                setEditingConsumption(null);
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                if (editingConsumption) {
                  const input = document.getElementById('edit-qty-input') as HTMLInputElement;
                  if (input) {
                    const qty = parseInt(input.value, 10);
                    handleUpdateConsumption(qty);
                  }
                }
              }}
              data-modal-default="true"
            >
              Actualizar
            </button>
          </>
        }
      >
        {editingConsumption && (
          <div>
            <p><strong>Piedra:</strong> {editingConsumption.stone.cod_piedra} - {editingConsumption.stone.nombre_piedra}</p>
            <p><strong>Semana:</strong> {formatDateUtil(editingConsumption.week_start_date)}</p>
            <div className="input-group">
              <label className="input-label">Ristras Consumidas *</label>
              <input
                id="edit-qty-input"
                type="number"
                min="0"
                step="1"
                className="input"
                defaultValue={editingConsumption.qty_ristras}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                required
              />
            </div>
          </div>
        )}
      </Modal>

      <StockChangeModal
        isOpen={isStockChangeModalOpen}
        onClose={() => setIsStockChangeModalOpen(false)}
        title="Cambios de Stock - Consumo Semanal de Piedras Registrado"
        changes={stockChanges}
      />
    </div>
  );
}

